import { restapi } from './restapi';
import {
	type DedobUser,
	type Document,
	type  Edition,
	type EditionDef,
	EditionState,
	type  EmailRecipients,
	hasPermissions,
	type OpUpdate,
	Permissions,
	type Section,
	Topic,
	type TopicInfo,
	type TopicSection,
	type TopicTitle,
} from './types';
import { type EnumHashMap } from '../utils';
import { checkSection } from './utils';


export function getDedobUser() {
	return restapi.get<DedobUser>(`dedob-user/`).then(res => res.data);
}

export function getDedobCss() {
	return restapi.get<{ css: string }>(`dedob-css/`).then(res => res.data);
}

export function getDocument(docId: string) {
	return restapi.get<Document>(`document/${docId}/`).then(res => res.data);
}

export function getDocByEdition(edId: number) {
	const cfg = {params: {edition_id: edId}};
	return restapi.get<Document>(`document/0/`, cfg).then(res => res.data);
}

function parseTopics(info: TopicInfo[]): { emergencies: TopicTitle[], others: TopicInfo[] } {
	const relevant: Topic[] = [
		Topic.scaleUp,  // from Jan 2022
		Topic.corporateScaleUp,  // from Jan 2026
		Topic.l3emergency,
		Topic.corporateAttention,  // from Jan 2022
		Topic.corporateFocus,  // from Jan 2026
		Topic.l2emergency,
		Topic.monitoring,
		Topic.actionResponse,  // from Jan 2022
		Topic.corporatePreparedness,  // from Jan 2026
	];
	const allEmergencies: TopicTitle[] = [];
	const allOthers: TopicInfo[] = [];

	info.forEach(i => {
		if (relevant.includes(i.topic)) {
			allEmergencies.push(
				...i.titles.filter(t => !t.drafter)  // filter out created emergencies
					.map(tt => ({...tt, mainTopicId: i.id})),  // remind main topic ids
			);
		} else {
			allOthers.push({
				...i,
				titles: [],
				feCleanTitles: i.titles.filter(t => t.id > 0).map(t => t.title),
			});
		}
	});

	return {emergencies: allEmergencies, others: allOthers};
}

export function getTopics(docId: string) {
	const cfg = {params: {slug: docId}};
	return restapi.get<TopicInfo[]>(`topic/`, cfg)
		.then(res => parseTopics(res.data));
}

export function publish(docId: string, addresses: EmailRecipients, when?: string) {
	const params = {...addresses, when: when || ''};
	return restapi.post<{}>(`document/${docId}/current-publish/`, params).then(res => res.data);
}

export function createRevision(docId: string) {
	return restapi.post<{}>(`document/${docId}/create-revision/`).then(res => res.data);
}

// Utils
export const editions: EditionDef[] = [
	{name: '1st'}, {name: '2nd'}, {name: '3rd'},
	{name: 'Rv1'}, {name: 'Rv2'}, {name: 'Rv3'},
];

export function isRevision(d: EditionDef | Edition) {
	return d.name.startsWith('Rv');
}

const topicLevel: EnumHashMap<Topic, number> = {
	[Topic.highlights]: 1,
	[Topic.redAlert]: 11,
	[Topic.orangeAlert]: 12,
	[Topic.yellowAlert]: 13,
	[Topic.l3emergency]: 50,
	[Topic.l2emergency]: 50,
	[Topic.monitoring]: 50,
	[Topic.other]: 50,
	[Topic.naturalHazard]: 50,

	// from Jan 2022
	[Topic.scaleUp]: 50,
	[Topic.corporateAttention]: 50,
	[Topic.actionResponse]: 50,

	// from Jan 2026
	[Topic.corporatePreparedness]: 50,
	[Topic.corporateScaleUp]: 50,
	[Topic.corporateFocus]: 50,
}

function compareSectionForPublish(a: Section, b: Section): number {
	const levelA = topicLevel[a.topic] || 100;
	const levelB = topicLevel[b.topic] || 100;
	if (levelA !== levelB) return levelA - levelB;
	if (a.orderInDoc !== b.orderInDoc) return a.orderInDoc - b.orderInDoc;
	return a.id - b.id;
}

const topicsToOrder: Topic[] = [
	Topic.l3emergency, Topic.l2emergency, Topic.monitoring,
	Topic.naturalHazard, Topic.other,
	Topic.scaleUp, Topic.corporateAttention, Topic.actionResponse,  // from Jan 2022
	Topic.corporatePreparedness, Topic.corporateScaleUp, Topic.corporateFocus,  // from Jan 2026
];

export function allSectionsToOrder(ed: Edition): Section[] {
	const sections = (ed?.topicSectionSet || []).map(t => t.contentSet).flat();
	return sections
		.filter(s => topicsToOrder.includes(s.topic))
		.sort(compareSectionForPublish);
}

export function allFixedTopicSections(ed: Edition): TopicSection[] {
	return ed.topicSectionSet.filter(ts => !topicsToOrder.includes(ts.topic));
}

export function allSectionForSummary(ed: Edition): Section[] {
	const sections = (ed?.topicSectionSet || []).map(t => t.contentSet).flat();
	return sections.sort(compareSectionForPublish);
}

export function getUserOwnedSections(docData?: Document, user?: DedobUser): Section[] {
	if (!user) return [];
	const currentEd = (docData?.editionSet || []).find(e => e.state === EditionState.current);
	if (!currentEd) return [];
	return (currentEd.topicSectionSet || [])
		.map(t => t.contentSet)
		.flat()
		.filter(s => s.owner === user.username);
}

export function getOpUpdates(country: number) {
	const cfg = {params: {countries: country}};
	return restapi.get<OpUpdate[]>(`operational-updates/`, cfg).then(res => res.data);
}

export function traceOpUpdateUsage(section: Section, update: OpUpdate) {
	const data = {
		section: section.topic + ' - ' + section.title,
		country: update.country,
		update: update.title,
	};
	restapi.post('operational-updates-audit/', data);
}

export function hasConfidentialContent(ed: Edition): boolean {
	if (ed.name === editions[0].name) {  // 1st Edition
		return false;  // nothing is too confidential for 1st ed
	}
	const allowInternal = (ed.name === editions[1].name); // 2nd Edition allow internal content
	return ed.topicSectionSet.some(tss => tss.contentSet.some(
		s => !checkSection(s, allowInternal),
	));
}

export function getNextEditionDef(d: Document): EditionDef | undefined {
	let nextDef: EditionDef | undefined = undefined;
	editions.forEach((def, i) => {  // go through editions in order
		const ed = (i < editions.length - 1) ?  // get this edition (skip the last one)
			d.editionSet?.find(e => e.name === def.name)
			: undefined;
		if (ed) {  // if the edition is there update the nextDef to be considered
			nextDef = (ed.state === EditionState.published) ?
				editions[i + 1]  // only if
				: undefined;
		}
	});
	return nextDef;
}

export function userCanAddSection(user: DedobUser) {
	return user.isSuperUser ||
		hasPermissions(user, Permissions.addSection);
}

export function userCanClearAll(user: DedobUser) {
	return user.isSuperUser ||
		hasPermissions(user, [Permissions.clearSection, Permissions.submitAndClearSection, Permissions.publishSection]);
}

export function userCanOrderSections(user: DedobUser) {
	return userCanAddSection(user) || userCanClearAll(user);
}

export function userCanPublish(user: DedobUser) {
	return user.isSuperUser ||
		hasPermissions(user, Permissions.publishSection);
}

export function userCanCreateRevision(user: DedobUser) {
	return user.isSuperUser ||
		hasPermissions(user, Permissions.createRevision);
}

